<script setup>
import { WButton } from '@alice-health/wonderland-vue'
import { Extension } from '@tiptap/core'
import Image from '@tiptap/extension-image'
import Link from '@tiptap/extension-link'
import Mention from '@tiptap/extension-mention'
import Placeholder from '@tiptap/extension-placeholder'
import StarterKit from '@tiptap/starter-kit'
import { Editor, EditorContent } from '@tiptap/vue-3'
import debounce from 'lodash/debounce'
import { DOMParser } from 'prosemirror-model'
import { PluginKey } from 'prosemirror-state'
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'

import UploadInput from '../uploader/UploadInput.vue'
import MentionList from './MentionList.vue'

const props = defineProps({
  placeholder: { type: String, default: '' },
  actions: {
    type: Object,
    default: () => ({ bold: false, italic: false, file: false })
  },
  disabled: {
    type: Boolean,
    default: false
  },
  enterBreakLine: {
    type: Boolean,
    default: true
  },
  autoFocus: {
    type: Boolean,
    default: false
  },
  options: { type: Array, default: undefined },
  filterOptions: { type: Function, default: undefined },
  transformOption: { type: Function, default: undefined },
  mentionList: {
    type: Array,
    default: () => []
  },
  updateValue: {
    type: Boolean,
    default: false
  },
  value: { type: String, default: undefined },
  id: { type: String, default: undefined },
  focus: {
    type: Boolean,
    default: false
  }
})
const emit = defineEmits(['attachmentFunction', 'pressEnter', 'typing'])

const mentionListRef = ref(null)
const suggestionListRef = ref(null)
const uploadInput = ref(null)

const state = reactive({
  editor: null,
  isSuggestionEnter: false,
  html: '',
  mentionProps: {
    query: '',
    items: [],
    command: () => undefined
  },
  suggestionProps: {
    query: '',
    items: [],
    command: () => undefined
  }
})

const shouldDisable = computed(() => {
  return props.disabled || null
})

watch(
  () => props.mentionList,
  (value, oldValue) => {
    if (value.length !== oldValue?.length) getEditor()
  }
)
watch(
  () => props.placeholder,
  value => {
    state.editor.commands.updatePlaceholder(value)
  }
)
watch(
  () => props.disabled,
  () => {
    state.editor.setEditable(!props.disabled)
  }
)
watch(
  () => props.updateValue,
  newValue => {
    if (newValue) {
      handleDraftMessage()
      state.editor.commands.focus('end')
    }
  }
)
watch(
  () => props.focus,
  value => {
    if (value) {
      state.editor.commands.focus('end')
    }
  }
)
onMounted(() => {
  getEditor()
})

onBeforeUnmount(() => {
  state.editor?.destroy()
})
const showFilePrompt = () => {
  uploadInput.value.showFilePrompt()
}
const attachmentChange = files => {
  emit('attachmentFunction', files[0])
}
const reset = () => {
  state.html = ''
  state.editor.commands.clearContent()
}
const sendMessage = () => {
  emit('pressEnter', {
    message: state.html,
    mentions: getMentions(state.html)
  })
  try {
    reset()
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Richtext error on sendMessage', error)
  }
}
const getMentions = message => {
  const mentions = props.mentionList
    ?.filter(({ fullName }) => message?.includes(`>@${fullName}<`))
    .map(item => item.id)

  return mentions
}
const evalElements = stringElements => {
  const template = document.createElement('template')
  template.innerHTML = stringElements.trim()
  return template.content
}
const handleSelectSuggestion = option => {
  let selectedOption = option
  if (props.transformOption) {
    selectedOption = props.transformOption(option)
  }
  state.editor.commands.clearContent()
  const element = evalElements(selectedOption.value)
  const { schema, tr } = state.editor.state
  const slice = DOMParser.fromSchema(schema).parseSlice(element)
  const transaction = tr.insert(0, slice.content)
  state.editor.view.dispatch(transaction)

  state.editor.commands.focus()
}
const getOptionsDebounced = debounce(async function (query) {
  const result = await props.filterOptions(query)

  if (state.isSuggestionEnter) {
    state.suggestionProps.items = result
  }
}, 300)
const handleDraftMessage = () => {
  if (props.value) {
    state.editor.commands.setContent(props.value)
    state.html = state.editor.getHTML()
  }
}
const getEditor = () => {
  if (state.editor) state.editor.destroy()
  state.editor = new Editor({
    editable: !props.disabled,
    onUpdate: ({ editor }) => {
      state.html = editor.getHTML()

      state.html = state.html.replace(/^(%)/g, '')
      emit('typing', state.html)
    },
    extensions: [
      StarterKit,
      Link,
      Image.configure({
        inline: true,
        allowBase64: true
      }),
      Placeholder.extend({
        addCommands() {
          return {
            updatePlaceholder: props => options => {
              options.editor.options.extensions.find(
                ex => ex.name === 'placeholder'
              ).options.placeholder = props
            }
          }
        }
      }).configure({
        placeholder: props.placeholder,
        showOnlyCurrent: false,
        showOnlyWhenEditable: false
      }),
      !props.enterBreakLine
        ? Extension.create({
            addKeyboardShortcuts() {
              return {
                'Shift-Enter': ({ editor }) => {
                  if (!editor.isActive('listItem')) return false
                  const { schema, tr } = editor.view.state
                  const transaction = tr.replaceSelectionWith(
                    schema.nodes.listItem.createAndFill()
                  )
                  editor.view.dispatch(transaction)
                  return true
                },
                Enter: () => {
                  sendMessage()
                  return true
                }
              }
            }
          })
        : null,
      props.mentionList.length
        ? Mention.extend({
            name: 'mention'
          }).configure({
            HTMLAttributes: {
              class: 'mention'
            },
            suggestion: {
              pluginKey: new PluginKey('mention'),
              allowSpaces: true,
              items: ({ query }) => {
                const result = props.mentionList.filter(item =>
                  item.fullName.toLowerCase().includes(query.toLowerCase())
                )
                return result
              },
              render: () => {
                return {
                  onStart: props => {
                    state.mentionProps = props
                  },
                  onUpdate: props => {
                    state.mentionProps = props
                  },
                  onExit: () => {
                    state.mentionProps = {
                      query: null,
                      items: [],
                      command: () => undefined
                    }
                  },
                  onKeyDown: props => {
                    return mentionListRef.value.onKeyDown(props)
                  }
                }
              }
            }
          })
        : null,
      Mention.extend({
        name: 'suggestion'
      }).configure({
        suggestion: {
          pluginKey: new PluginKey('suggestion'),
          char: '%',
          allowSpaces: true,
          items: ({ query }) => {
            if (props.filterOptions) {
              getOptionsDebounced(query)
              return state.suggestionProps.items
            }

            return props.options?.filter(item => item.key.includes(query))
          },
          render: () => {
            return {
              onStart: props => {
                state.suggestionProps = props
                state.isSuggestionEnter = true
              },
              onUpdate: props => {
                state.suggestionProps = props
              },
              onExit: () => {
                state.suggestionProps = {
                  query: null,
                  items: [],
                  command: () => undefined
                }
                state.isSuggestionEnter = false
              },
              onKeyDown: props => {
                return suggestionListRef.value.onKeyDown(props)
              }
            }
          }
        }
      })
    ].filter(ext => ext != null)
  })
  if (props.autoFocus) state.editor.commands.focus('end')
  handleDraftMessage()
}

defineExpose({ reset })
</script>

<template>
  <div v-if="state.editor" class="richtext" :disabled="shouldDisable">
    <slot></slot>
    <MentionList
      v-bind="state.suggestionProps"
      ref="suggestionListRef"
      :handle-select-item="handleSelectSuggestion"
      empty-message="Nenhuma opção encontrada"
    >
      <template #default="option">
        {{ option.key }}:
        <div v-html="option.value" />
      </template>
    </MentionList>
    <MentionList v-bind="state.mentionProps" ref="mentionListRef">
      <template #default="option">
        {{ option.fullName }}
      </template>
    </MentionList>
    <EditorContent
      :id="id"
      class="richtext__content"
      :spellcheck="true"
      :editor="state.editor"
    />
    <div :editor="state.editor" :keep-in-bounds="true">
      <div class="richtext__menubar">
        <WButton
          class="richtext__menubar-button"
          variant="tertiary"
          size="small"
          :disabled="shouldDisable"
          :active="state.editor.isActive('heading', { level: 3 }) || null"
          @click="
            state.editor.chain().focus().toggleHeading({ level: 3 }).run()
          "
        >
          Título
        </WButton>
        <WButton
          class="richtext__menubar-button"
          variant="tertiary"
          size="small"
          :disabled="shouldDisable"
          :active="state.editor.isActive('bulletList') || null"
          icon="icBullets"
          icon-button
          @click="state.editor.chain().focus().toggleBulletList().run()"
        />
        <WButton
          class="richtext__menubar-button"
          variant="tertiary"
          size="small"
          :disabled="shouldDisable"
          :active="state.editor.isActive('orderedList') || null"
          icon="icNumbering"
          icon-button
          @click="state.editor.chain().focus().toggleOrderedList().run()"
        />
        <WButton
          v-if="actions.bold"
          class="richtext__menubar-button"
          variant="tertiary"
          size="small"
          :disabled="shouldDisable"
          :active="state.editor.isActive('bold') || null"
          icon="icBold"
          icon-button
          @click="state.editor.chain().focus().toggleBold().run()"
        />
        <WButton
          v-if="actions.italic"
          class="richtext__menubar-button"
          variant="tertiary"
          size="small"
          :disabled="shouldDisable"
          :active="state.editor.isActive('italic') || null"
          icon="icItalic"
          icon-button
          @click="state.editor.chain().focus().toggleItalic().run()"
        />
        <UploadInput
          ref="uploadInput"
          hidden
          name="fileUpload"
          @upload="attachmentChange"
        />
        <WButton
          v-if="actions.file"
          class="richtext__menubar-button"
          variant="tertiary"
          size="small"
          :disabled="shouldDisable"
          icon="icAttachment"
          data-testid="upload-button"
          icon-button
          @click="showFilePrompt"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss">
@import '@/style/_non-rendering';

.richtext {
  @extend %body-small;
  background-color: var(--background-color, var(--gl-color-shades-white));
  border: 1px solid var(--gl-color-shades-gray-20);
  color: var(--text-color, var(--gl-color-shades-gray-50));
  border-radius: 8px;
  position: relative;
  transition: border-color 0.2s;

  &:hover {
    border-color: var(--border-focus-color, var(--gl-color-shades-gray-100));
  }

  &:not([disabled]):focus-within {
    border-color: var(--gl-color-shades-magenta-60);
    color: var(--text-color, var(--gl-color-shades-gray-100));
    outline: 0;
  }

  &__content {
    overflow-y: auto;
    max-height: 170px;
    outline: 0;

    .ProseMirror {
      height: 100%;
      padding: 12px 12px 4px;
      outline: 0;

      p {
        margin: 0;
      }
      &:focus-visible {
        outline: 0;
      }
    }

    .ProseMirror-focused {
      outline: none;
      border: 0;
      box-shadow: none;
    }

    &:focus {
      outline: none;
      border: 1px solid var(--gl-color-shades-magenta-60);
      box-shadow: none;
    }

    .mention {
      color: var(--gl-color-shades-magenta-00);
    }
  }

  &__menubar {
    display: flex;
    gap: var(--gl-spacing-01);
    padding: 4px;
    min-height: 38px;
    opacity: 0;
    transition: opacity 0.3s;
    background-color: #ffffffa8;
    border-radius: 8px;

    &-button {
      color: var(--text-color, #000000);
    }
  }

  &:not([disabled]):focus-within &__menubar {
    opacity: 1;
  }
}

.mention-suggestion {
  color: var(--text-color);
  opacity: 0.6;
}

.suggestion-list {
  padding: 8px;
  border: 1px solid var(--border-color, var(--gl-color-shades-gray-20));
  border-radius: 8px;
  position: absolute;
  bottom: 100px;
  background-color: var(--background-color, var(--gl-color-shades-gray-00));
  width: 90%;
  max-height: 300px;
  overflow-y: scroll;
  z-index: 3;

  &__no-results {
    padding: 8px;
  }
  &__item {
    border-radius: 5px;
    padding: 8px;
    margin-bottom: 0.2rem;
    cursor: pointer;
    &:last-child {
      margin-bottom: 0;
    }
    &.is-selected,
    &:hover {
      background-color: var(--border-color, var(--gl-color-shades-gray-20));
    }
    &.is-empty {
      opacity: 0.5;
    }
  }
}
.ProseMirror p.is-editor-empty:first-child::before {
  content: attr(data-placeholder);
  float: left;
  pointer-events: none;
  height: 0;
}

.richtext__menubar-button[active='true'] {
  background-color: var(--gl-color-shades-gray-10);
  border-radius: 50px;
}
</style>
